import React, { useEffect, useState } from 'react';
import { getNsCustomer, getCustomerHistory, createNsCustomer } from '../services/api';

interface CustomerPanelProps {
  customerId: number | null;
  customerName: string | null;
  senderId: string;
  // Called after a new customer is created so the feed can refresh and resolve
  // the conversation.
  onCreated?: () => void;
}

// Base sale unit inferred from J&J's SKU naming convention — the trailing unit
// code on the item name (e.g. "22% LAYER 50#BG" -> bags, "20#BKT" -> buckets,
// "PERSIMMON 32OZ" -> unknown). Only codes we positively recognize get a unit
// word; anything else shows the bare count rather than risk a WRONG label
// (accuracy over coverage — a "bag" stamped on a bucket is worse than no noun).
const UNIT_WORDS: Record<string, [string, string]> = {
  BG: ['bag', 'bags'], BAG: ['bag', 'bags'], BAGS: ['bag', 'bags'],
  BKT: ['bucket', 'buckets'], BUCKET: ['bucket', 'buckets'],
  TOTE: ['tote', 'totes'], TOTES: ['tote', 'totes'],
  DRUM: ['drum', 'drums'], PAIL: ['pail', 'pails'], JUG: ['jug', 'jugs'],
  CS: ['case', 'cases'], CASE: ['case', 'cases'], EA: ['each', 'each'],
};
const baseUnitWords = (it: any): [string, string] | null => {
  const name = String(it?.displayname || it?.item_name || '').toUpperCase();
  const m = name.match(/([A-Z]+)\s*$/); // trailing run of letters = unit code
  return (m && UNIT_WORDS[m[1]]) || null;
};

// Format a history row's TOTAL 12-month quantity with accurate units — the
// combined volume across EVERY order in the window, not a single order.
//  - When the item's base unit is known AND the total is a clean multiple of its
//    bags/pallet factor, lead with pallets + the base-unit equivalent, e.g.
//    "×54 pallets (2,160 bags)".
//  - Otherwise show the total in its own unit (non-pallet-multiple, or no pallet
//    factor), e.g. "×8 buckets".
//  - Unknown unit code -> bare count (no false unit). No quantity (older deploy
//    / no line data) -> fall back to the frequency count so it never renders blank.
const fmtHistoryQty = (it: any): string => {
  const qty = typeof it?.total_qty === 'number' ? it.total_qty
    : typeof it?.typical_qty === 'number' ? it.typical_qty : null;
  if (qty == null || qty <= 0) return `×${it?.order_count ?? '—'}`;
  const words = baseUnitWords(it);
  const bpp = typeof it?.bags_per_pallet === 'number' ? it.bags_per_pallet : null;
  if (words && bpp && bpp > 0 && qty % bpp === 0) {
    const pallets = qty / bpp;
    return `×${pallets.toLocaleString()} pallet${pallets === 1 ? '' : 's'} (${qty.toLocaleString()} ${words[1]})`;
  }
  if (words) return `×${qty.toLocaleString()} ${qty === 1 ? words[0] : words[1]}`;
  return `×${qty.toLocaleString()}`;
};

// Hover breakdown: total volume + order count + usual single-order size.
const histTooltip = (it: any): string => {
  const n = it?.order_count ?? '—';
  const parts = [`12-mo total across ${n} order${n === 1 ? '' : 's'}`];
  const words = baseUnitWords(it);
  const typ = typeof it?.typical_qty === 'number' ? it.typical_qty : null;
  if (typ && typ > 0) {
    const u = words ? ` ${typ === 1 ? words[0] : words[1]}` : '';
    parts.push(`~${typ.toLocaleString()}${u} per order avg`);
  }
  return parts.join(' · ');
};

// Surfaces the NetSuite context the backend already has for a contact:
// company/contact details + recent order history. Read-only, lazy-loaded when
// the conversation is expanded.
const CustomerPanel: React.FC<CustomerPanelProps> = ({ customerId, customerName, senderId, onCreated }) => {
  const [customer, setCustomer] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Customer-360 fetch is decoupled from history: a bad/stale customer_id (e.g. a
  // mis-stamped list-ref like "2") makes getNsCustomer 404/500, but that must NOT
  // suppress the history render for an otherwise-valid id. Track its miss softly.
  const [custUnavailable, setCustUnavailable] = useState(false);

  // Create-customer form state (only used when this sender isn't linked yet).
  const [showForm, setShowForm] = useState(false);
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(senderId || '');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const handleCreate = async () => {
    if (!company.trim()) { setSaveErr('Company name is required'); return; }
    setSaving(true); setSaveErr('');
    try {
      await createNsCustomer({
        sender_id: senderId,
        company_name: company.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
      });
      setShowForm(false);
      onCreated?.();
    } catch (e: any) {
      setSaveErr(e?.response?.data?.error || 'Could not create customer');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true); setError(false); setCustUnavailable(false);
    // Settle the two loads INDEPENDENTLY: a getNsCustomer failure (404/500 on a
    // mis-stamped id) must never reject away a history render that would otherwise
    // succeed. History is the load-bearing panel content; the customer-360 header
    // degrades to a soft "details unavailable" state on its own miss.
    Promise.allSettled([getNsCustomer(customerId), getCustomerHistory(customerId, 12)])
      .then(([custRes, histRes]) => {
        if (cancelled) return;
        if (custRes.status === 'fulfilled') {
          setCustomer(custRes.value);
        } else {
          setCustomer(null);
          setCustUnavailable(true);
        }
        if (histRes.status === 'fulfilled') {
          setItems(histRes.value || []);
        } else {
          setItems([]);
          setError(true);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId]);

  // Not linked to a NetSuite customer (e.g. an unknown/new number) — offer to
  // create one and link it to this sender.
  if (!customerId) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 mb-2 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500">
            <span className="font-medium text-gray-700">{customerName || senderId}</span>
            {' '}— not linked to a NetSuite customer yet.
          </span>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex-shrink-0 text-sm font-medium text-brand-700 hover:text-brand-800 whitespace-nowrap"
            >
              + Create customer
            </button>
          )}
        </div>

        {showForm && (
          <div className="mt-2 space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Company name *</label>
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. Copiah-Lincoln Farms"
                className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-0.5">Phone</label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-0.5">Email</label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="optional"
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
            {saveErr && <div className="text-xs text-red-600">{saveErr}</div>}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowForm(false); setSaveErr(''); }}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !company.trim()}
                className="px-3 py-1.5 text-sm font-medium text-white bg-brand-700 rounded-full hover:bg-brand-800 disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create in NetSuite'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-brand-100 bg-brand-50/40 px-3 py-2 mb-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-brand-800 truncate">
          {customer?.companyname || customer?.entityid || customerName || senderId}
        </span>
        <span className="text-xs text-gray-500 flex-shrink-0">Customer #{customerId}</span>
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
        {custUnavailable && (
          <span className="text-gray-400 italic">Customer details unavailable.</span>
        )}
        {customer?.phone && <span>📞 {customer.phone}</span>}
        {customer?.email && <span className="truncate">✉ {customer.email}</span>}
      </div>

      <div className="mt-2">
        <div className="text-xs font-semibold text-gray-600 mb-1">Recent orders (12 mo)</div>
        {loading && <div className="text-xs text-gray-400">Loading…</div>}
        {error && <div className="text-xs text-red-500">Couldn't load history.</div>}
        {!loading && !error && items.length === 0 && (
          <div className="text-xs text-gray-400">No recent orders found.</div>
        )}
        {!loading && !error && items.length > 0 && (
          <ul className="space-y-0.5">
            {items.slice(0, 6).map((it: any, i: number) => (
              <li key={i} className="flex items-center justify-between text-xs text-gray-700 gap-2">
                <span className="truncate">{it.displayname || it.item_name || `Item ${it.item_id}`}</span>
                <span
                  className="text-gray-400 flex-shrink-0 whitespace-nowrap"
                  title={histTooltip(it)}
                >
                  {fmtHistoryQty(it)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CustomerPanel;
